import React from 'react';
import { RailsBlock } from '@sls-react/typings/services/rails/tile';
import BlockSection from '@sls-react/components/legos/BlockSection';
import Image from '@sls-react/components/Image';
import ShopLink from '@sls-react/components/Links/ShopLink';
import useFacetFiltering from '@sls-react/contexts/facetFiltering/useFacetFiltering';
import A from '@sls-react/components/Links/A';
import ArrowRightIcon from '@sls-react/static/svg/ArrowRightIcon';
import MobileOnlyFeatheredOverflowContainer from '@sls-react/components/MobileOnlyFeatheredOverflowContainer';
import { useQuery } from 'react-query';
import { fetchModelsByPosition } from '@sls-react/api/models';

interface Props {
  block: RailsBlock;
  headerType?: 'h1' | 'h2' | 'h3';
}

const ModelsBlock: React.FC<Props> = ({ block, headerType }) => {
  const { filterState, lander } = useFacetFiltering();
  const categoryId =
    Number(
      block.query?.category_id ||
        block.category?.id ||
        filterState?.category?.[0] ||
        lander?.query?.category?.[0] ||
        lander?.category?.id,
    ) || '';

  const brandId =
    Number(
      block.query?.brand_id ||
        lander?.query?.brand?.[0] ||
        filterState?.brand?.[0],
    ) || '';

  const { data: models } = useQuery(['shop-models', categoryId, brandId], () =>
    fetchModelsByPosition(categoryId, brandId, 8),
  );

  if (!models || models.length === 0) {
    return null;
  }

  const modelCx =
    'mr-2 md:mb-2 flex items-center space-x-2 rounded-full border shadow-sm pl-2 pr-4 py-1.5 hover:shadow';

  const filteredModels = models.filter(
    model => model.primary_image || model.images?.length! > 0,
  );

  return (
    <BlockSection
      block={block}
      headerType={headerType}
      showCta={false}
      id="popular-models"
    >
      <MobileOnlyFeatheredOverflowContainer
        className="flex p-px overflow-x-auto overflow-y-visible md:flex-wrap md:overflow-visible"
        css={`
          & > * {
            flex-shrink: 0;
          }
        `}
      >
        {filteredModels.map(model => (
          <ShopLink key={model.id} className={modelCx} path={model.url}>
            <Image
              className="w-10 h-10 border rounded-full"
              src={
                model.primary_image?.thumb_url || model.images?.[0]?.thumb_url
              }
              useFastly={{
                height: 40,
                width: 40,
              }}
            />

            <div>
              <div className="mb-1 text-xs font-semibold">
                {model.brand_name} {model.name}
              </div>
              <div className="text-2xs text-slate-green-500">
                {model.category_name}
              </div>
            </div>
          </ShopLink>
        ))}

        {block.destination && (
          <A
            className={`${modelCx} pl-6 pr-4 text-blue-500 font-semibold`}
            href={block.destination}
            rel="noindex"
          >
            <span>{block.title}</span>
            <ArrowRightIcon />
          </A>
        )}
      </MobileOnlyFeatheredOverflowContainer>
    </BlockSection>
  );
};

export default ModelsBlock;
